import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/auth-context';
import { useRefereeStatsQuery } from '../hooks/use-stats';
import { useStreakQuery } from '../hooks/use-daily';
import { useRefereeClipsQuery } from '../hooks/use-clips';
import { useTrendQuery } from '../hooks/use-trend';
import { HomeStatCards } from '../components/home/HomeStatCards';
import { TodaysClipHero } from '../components/home/TodaysClipHero';
import { UpcomingWebinarsCard } from '../components/home/UpcomingWebinarsCard';
import { RuleMasteryCard } from '../components/home/RuleMasteryCard';
import { RecommendedClips } from '../components/home/RecommendedClips';
import { SponsorSlot } from '../components/shared/SponsorSlot';

// Time-of-day greeting key. Local clock — matches the referee's day, and keeps the header feeling
// live without depending on any server data. Returns a translation key, not text — the caller
// resolves it via t() so the greeting is in the referee's own language.
function greetingKey(): 'home.greeting.morning' | 'home.greeting.afternoon' | 'home.greeting.evening' {
  const h = new Date().getHours();
  if (h < 12) return 'home.greeting.morning';
  if (h < 18) return 'home.greeting.afternoon';
  return 'home.greeting.evening';
}

// The referee (and federation_admin) home dashboard. Real data drives every card, including both
// stat-tile sparklines (GET /v1/me/trend). super_admin never lands here (App's RoleHome routes it
// to the console instead).
export function HomePage() {
  const { t } = useTranslation();
  const { user, role } = useAuth();
  const firstName = user?.full_name?.split(/\s+/)[0] ?? t('home.greeting.fallbackName');

  // Only clip-answering roles have personal stats/mastery; gate the queries so they don't fire (and
  // 403) for anyone else. In practice this page only mounts for referee/federation_admin.
  const eligible = role === 'referee' || role === 'federation_admin';

  const statsQuery = useRefereeStatsQuery(eligible);
  const stats = statsQuery.data;

  // Streak card — its own query (GET /v1/me/streak), same role gate as the stats.
  const streakQuery = useStreakQuery(eligible);

  // The two sparklines' real history (GET /v1/me/trend), same role gate.
  const trendQuery = useTrendQuery(eligible);

  // Total published clips in the library — the denominator for the "clips reviewed" card. Reuses the
  // referee library query (no rule filter), so it shares cache with the browse page.
  const libraryQuery = useRefereeClipsQuery(undefined);
  const totalClips = libraryQuery.data?.length;

  const weakest = stats?.weakest_rule ?? null;

  return (
    <div className="space-y-5">
      {/* Greeting + a partner banner filling the empty space beside it (stacks under the greeting on
          narrow screens). */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-stretch lg:justify-between">
        <div>
          <h1 className="font-display text-3xl text-ink">
            {t('home.greeting.line', { greeting: t(greetingKey()), name: firstName })}
          </h1>
          <p className="mt-1 text-sm text-muted">{t('home.subtitle')}</p>
        </div>
        <SponsorSlot variant="banner" className="w-full min-w-0 lg:flex-1" />
      </div>

      <HomeStatCards
        stats={stats}
        totalClips={totalClips}
        loading={statsQuery.isLoading}
        streak={streakQuery.data}
        streakLoading={streakQuery.isLoading}
        trend={trendQuery.data}
      />

      {/* Left: today's assigned clip (answered inline). Right: the two live cards stacked. Align to
          the top so the shorter right column doesn't stretch to the tall inline answer panel. */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 lg:items-start">
        <TodaysClipHero enabled={eligible} />
        <div className="space-y-5">
          <UpcomingWebinarsCard />
          <RuleMasteryCard />
        </div>
      </div>

      {/* Rendered unconditionally: with no weakest rule (or no clips tagged to it) the section
          falls back to a random library sample and only hides itself when the library is empty. */}
      <RecommendedClips ruleArticleId={weakest?.rule_article_id} ruleNumber={weakest?.article_number} />
    </div>
  );
}
